import React from 'react';
import {Button, Icon, Menu, MenuItem, Text} from '@mc/ui-v4';
import {mcListFormatBulleted} from '@mc/react-ui-icons';
import {findNestedLabelById} from '../../../helpers/DataMutators';
import {UseQueryResult} from 'react-query';
import {CatalogItemVO, LayerVO} from '../../../api';
import {isUserDbLayer} from '../../../helpers/LayerConfigUtils';
import {LayerId} from '@mc/react-mapio';

type Props = {
    catalogLayersQuery: UseQueryResult<CatalogItemVO[]>;
    activeLayers: LayerId[];
    layerId: LayerId;
    setSelectedLayerId: (layerId: LayerId | null) => void;
    rowsData: any;
    layersConfigData: {[key: string]: LayerVO};
};

/**
 * Komponenta `AttributeTableTitle` zobrazuje název tabulky atributů a umožňuje výběr vrstvy z katalogu vrstev.
 *
 * @param catalogLayersQuery - Objekt s daty o vrstvách z katalogu (např. výsledek dotazu).
 * @param layerId - ID aktuálně vybrané vrstvy.
 * @param activeLayers - Pole ID aktivních vrstev.
 * @param setSelectedLayerId - Funkce pro nastavení vybrané vrstvy.
 * @param rowsData - Objekt obsahující metody pro práci s řádky a filtry tabulky.
 * @param layersConfigData - Konfigurační data vrstev, např. informace o rodičovských vrstvách.
 *
 * Komponenta vykresluje název aktuální vrstvy a v případě dostupných dalších vrstev z katalogu nabízí jejich výběr pomocí dropdown menu.
 * Menu je dynamicky generováno na základě struktury vrstev a jejich vztahů (rodič/dítě).
 *
 * Využívá optimalizaci pomocí `React.useMemo` pro generování položek menu.
 *
 * @returns JSX element s názvem vrstvy a případně dropdown menu pro výběr jiné vrstvy.
 */
export const AttributeTableTitle: React.FC<Props> = ({catalogLayersQuery, layerId, activeLayers, setSelectedLayerId, rowsData, layersConfigData}) => {
    /**
     * Funkce `renderChildMenuItem` generuje položky menu pro výběr vrstev.
     * Prochází zadané objekty vrstev (mohou být hierarchicky vnořené) a na základě jejich vlastností
     * rozhoduje, zda má být položka menu vykreslena, případně rekurzivně zpracovává potomky.
     *
     * - Pokud má vrstva potomky, ale aktuální vrstva je mezi nimi, nevykreslí se.
     * - Pokud má vrstva potomky, kteří jsou uživatelské vrstvy, zpracuje je rekurzivně.
     * - Pokud je vrstva aktivní, není rodičem a splňuje podmínky, vykreslí se jako položka menu.
     *
     * Každá položka menu při kliknutí nastaví vybranou vrstvu, resetuje řazení a filtry tabulky.
     */
    const renderChildMenuItem = (objects: CatalogItemVO[]) => {
        return objects?.flatMap(layer => {
            if (!layer || typeof layer !== 'object' || !layer.label) {
                console.warn('Invalid layer object:', layer);
                return [];
            }
            // Pokud má vrstva potomky a vlastní ID, zkontroluje, zda aktuální vrstva není mezi potomky nebo není sama sebou
            if (layer.children && layer.layerId) {
                if ((layer.children.length > 0 && layer.children.some(child => layerId === child.layerId)) || layerId === layer.layerId) {
                    return [];
                }
                return [
                    <MenuItem
                        key={`layerId-${layer.layerId}`}
                        text={`${layer.label}`}
                        onClick={() => {
                            rowsData.rows.setSort([]);
                            rowsData.filter.setFilter({}, {});
                            setSelectedLayerId(Number(layer.layerId));
                        }}
                    />,
                ];
            }
            // Pokud má vrstva potomky, kteří jsou uživatelské vrstvy, zpracuje je rekurzivně
            if (layer.children && layer.children.length > 0 && layer.children.some(child => isUserDbLayer(layersConfigData, Number(child.layerId)))) {
                return layer.children.flatMap(child => {
                    if (!child || typeof child !== 'object' || !child.label) {
                        console.warn('Invalid child object:', child);
                        return [];
                    }
                    const childLayerId = Number(child.layerId);
                    const childParentLayerId =
                        childLayerId && layersConfigData[childLayerId]?.parentLayerId ? Number(layersConfigData[childLayerId].parentLayerId) : undefined;
                    if (childParentLayerId === layerId) {
                        return [];
                    }
                    if (child.children && child.children.length > 0) {
                        return renderChildMenuItem(child.children);
                    }
                    if (
                        layerId === childLayerId ||
                        !activeLayers.includes(childLayerId) ||
                        !isUserDbLayer(layersConfigData, childLayerId) ||
                        childParentLayerId
                    ) {
                        return [];
                    }
                    return [
                        <MenuItem
                            key={`layerId-${child.id ?? child.layerId}`}
                            text={`${child.label}`}
                            onClick={() => {
                                rowsData.rows.setSort([]);
                                rowsData.filter.setFilter({}, {});
                                setSelectedLayerId(childLayerId);
                            }}
                        />,
                    ];
                });
            }
            // Zpracování hlavní vrstvy, pokud splňuje podmínky
            const itemLayerId = Number(layer.layerId || layer.id);
            const itemParentLayerId =
                itemLayerId && layersConfigData[itemLayerId]?.parentLayerId ? Number(layersConfigData[itemLayerId].parentLayerId) : undefined;
            if (layer.id === layerId || layerId === itemLayerId || !activeLayers.includes(itemLayerId) || !isUserDbLayer(layersConfigData, itemLayerId)) {
                return [];
            }
            if (itemParentLayerId === layerId || layersConfigData[itemLayerId]?.parentLayerId) {
                return [];
            }
            return [
                <MenuItem
                    key={`layerId-${layer.id ?? layer.layerId}`}
                    text={`${layer.label}`}
                    onClick={() => {
                        rowsData.filter.setFilter({}, {});
                        rowsData.rows.setSort([]);
                        setSelectedLayerId(itemLayerId);
                    }}
                />,
            ];
        });
    };

    const menuItems = React.useMemo(() => renderChildMenuItem(catalogLayersQuery?.data), [catalogLayersQuery?.data, layerId, activeLayers, layersConfigData]);
    const hasMenuItems = menuItems?.length > 0;

    return (
        <div className="att_button_dropdown att_table_title">
            {hasMenuItems ? (
                <Button
                    style="link"
                    dropdown
                    menu={<Menu>{menuItems}</Menu>}
                    icon={<Icon className="att_tab_icon ui-v4-button-icon-left" fixedWidth icon={mcListFormatBulleted} />}
                >
                    <span className="att_layer_name">{findNestedLabelById(catalogLayersQuery?.data, layerId) || 'neznámá'}</span>
                </Button>
            ) : (
                <Text className="ui-v4-button-text" icon={<Icon className="att_tab_icon ui-v4-button-icon-left" fixedWidth icon={mcListFormatBulleted} />}>
                    <span className="att_layer_name">{findNestedLabelById(catalogLayersQuery?.data, layerId) || 'neznámá'}</span>
                </Text>
            )}
        </div>
    );
};
